/**
 * Cheat sheet variant–aware checklist — auto N/A for checks that do not apply.
 *
 * Keeps one shared checklist; pre-fills N/A instead of hiding rows.
 */

import { isCheatSheetReviewArtefact } from "./artefactKind";
import { deriveRag } from "./deriveRag";
import type {
  ArtefactRating,
  ArtefactRatings,
  ArtefactReviewItem,
  PhaseCriteria,
} from "./types";

export type CheatSheetVariant = "full" | "full_with_proxy" | "data_only";

/** Stable check ids from CHEAT_SHEET_ARTEFACT_REVIEW_CRITERIA. */
export const CHEAT_SHEET_HOLDINGS_CHECKS = [
  "pp.art.holdings_vs_docs",
  "pp.art.sensible_matches",
  "pp.art.portfolio_generated",
  "pp.art.five_year_performance",
  "pp.art.asset_allocation",
  "pp.art.performance_vs_trustnet",
] as const;

export const CHEAT_SHEET_DATA_ONLY_CHECK = "pp.art.data_only_correct";
export const CHEAT_SHEET_PROXY_CHECK = "pp.art.proxy_correct";

export function cheatSheetVariantFromArtefact(
  artefact: ArtefactReviewItem,
  catalogueKey?: string | null,
): CheatSheetVariant | null {
  if (!isCheatSheetReviewArtefact(artefact, catalogueKey)) return null;

  const label = artefact.cheat_sheet_variant?.trim().toLowerCase();
  if (label === "data only") return "data_only";
  if (label === "full with proxy") return "full_with_proxy";
  if (label === "full") return "full";

  const mode = artefact.cheat_sheet_mode?.trim().toLowerCase().replace(/-/g, "_");
  if (mode === "data_only") return "data_only";
  if (artefact.cheat_sheet_uses_proxy) return "full_with_proxy";
  return "full";
}

export function cheatSheetVariantLabel(variant: CheatSheetVariant): string {
  if (variant === "data_only") return "Data only";
  if (variant === "full_with_proxy") return "Full with proxy";
  return "Full";
}

export function cheatSheetAutoNaCheckIds(variant: CheatSheetVariant): ReadonlySet<string> {
  const ids = new Set<string>();
  if (variant === "data_only") {
    for (const id of CHEAT_SHEET_HOLDINGS_CHECKS) ids.add(id);
    ids.add(CHEAT_SHEET_PROXY_CHECK);
    return ids;
  }
  ids.add(CHEAT_SHEET_DATA_ONLY_CHECK);
  if (variant === "full") {
    ids.add(CHEAT_SHEET_PROXY_CHECK);
  }
  return ids;
}

export function applyCheatSheetVariantAutoNa(
  artefact: ArtefactReviewItem,
  criteria: PhaseCriteria,
  rating: ArtefactRating,
  catalogueKey?: string | null,
): ArtefactRating {
  const variant = cheatSheetVariantFromArtefact(artefact, catalogueKey);
  if (!variant) return rating;

  const autoNaIds = cheatSheetAutoNaCheckIds(variant);
  const applicableIds = new Set(criteria.checks.map((check) => check.id));
  const answers = { ...rating.answers };
  let changed = false;

  for (const checkId of autoNaIds) {
    if (!applicableIds.has(checkId) || answers[checkId]) continue;
    answers[checkId] = "na";
    changed = true;
  }

  if (!changed) return rating;

  const derived = deriveRag(criteria, answers);
  const wasFollowing =
    rating.human_rag === null || rating.human_rag === rating.suggested_rag;
  return {
    ...rating,
    answers,
    suggested_rag: derived.rag,
    human_rag: wasFollowing ? derived.rag : rating.human_rag,
  };
}

export function applyCheatSheetVariantAutoNaForRun(
  run: {
    artefacts?: ArtefactReviewItem[] | null;
    artefact_review_criteria?: PhaseCriteria | null;
    catalogue_key?: string | null;
  },
  ratings: ArtefactRatings,
): ArtefactRatings {
  const criteria = run.artefact_review_criteria;
  const artefacts = run.artefacts ?? [];
  if (!criteria || !artefacts.length) return ratings;

  let changed = false;
  const next = { ...ratings };
  for (const artefact of artefacts) {
    if (!isCheatSheetReviewArtefact(artefact, run.catalogue_key)) continue;
    const current = next[artefact.artifact_id] ?? emptyArtefactRating();
    const patched = applyCheatSheetVariantAutoNa(artefact, criteria, current, run.catalogue_key);
    if (patched !== current) {
      next[artefact.artifact_id] = patched;
      changed = true;
    }
  }
  return changed ? next : ratings;
}

function emptyArtefactRating(): ArtefactRating {
  return {
    answers: {},
    check_comments: {},
    suggested_rag: null,
    human_rag: null,
    human_note: "",
    human_approval: null,
  };
}
